import React, { useMemo } from 'react';
import AuthLayout from '../../../components/layouts/AuthLayout';
import { ROUTE_PASSWORD_SUCCESS, ROUTE_DASHBOARD, ROUTE_MIGRATION } from '../../../constants';
import { AmplifyAuth, getAmplifyAuthData } from '../../../services/amplifyAuth';
import { catchYupErrors } from '../../../utils/validationUtils';
import SetPasswordForm from './SetPasswordForm';
import useQueryHistory from '../../../hooks/query/useQueryHistory';
import { useApplication } from '../../../services/application';
import { useAlertContext } from '../../../contexts/AlertContext';
import { ModalDialog } from '../../../components/common/Dialogs';
import { useTrans } from '../../../services/i18n';
import Grid from '@material-ui/core/Grid';
import { SpacingContainer } from '../../../components/common/Containers';
import Typography from '../../../components/common/Typography';
import {
  CODE_MISMATCH_EXCEPTION,
  EXPIRED_CODE_EXCEPTION,
  LIMITED_EXCEEDED_EXCEPTION_CODE,
  TOO_MANY_FAILED_ATTEMPTS_EXCEPTION_CODE,
  USER_ALREADY_CONFIRMED_CODE,
} from '../../../utils/exceptions';

const INVALID_INVITE_CODE_CODES = new Set([
  CODE_MISMATCH_EXCEPTION,
  EXPIRED_CODE_EXCEPTION,
  TOO_MANY_FAILED_ATTEMPTS_EXCEPTION_CODE,
  LIMITED_EXCEEDED_EXCEPTION_CODE,
]);

const SetPassword = () => {
  const { history, query = {} } = useQueryHistory();

  const application = useApplication();
  const { errorAlert } = useAlertContext();
  const email = useMemo(() => query?.email?.trim().replaceAll(' ', '+'), [query?.email]);
  const isInviteFlow = Boolean(query.invite);

  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState(false);
  const { trans } = useTrans();

  const authorizeAndRedirect = React.useCallback(
    async (password) => {
      const user = await AmplifyAuth.signIn(email, password);
      const { name } = await application.authorize(getAmplifyAuthData(user));
      if (!name) {
        history.push(ROUTE_MIGRATION.path);
      } else {
        history.push(ROUTE_DASHBOARD.path);
      }
    },
    [application, email, history]
  );

  const completeInviteWithTemporaryPassword = React.useCallback(
    async (temporaryPassword, password) => {
      const user = await AmplifyAuth.signIn(email, temporaryPassword);
      if (user.challengeName !== 'NEW_PASSWORD_REQUIRED') {
        throw new Error('Unexpected auth state');
      }

      const signedInUser = await AmplifyAuth.completeNewPassword(user, password);
      const { name } = await application.authorize(getAmplifyAuthData(signedInUser));
      if (!name) {
        history.push(ROUTE_MIGRATION.path);
      } else {
        history.push(ROUTE_DASHBOARD.path);
      }
    },
    [application, email, history]
  );

  const handleSubmit = React.useCallback(
    async (values, { setStatus, setErrors }) => {
      setLoading(true);
      setError(false);

      if (isInviteFlow) {
        if (!(email && values.verificationCode)) {
          setLoading(false);
          return;
        }

        try {
          const confirmResult = await application.call('user.confirmEmail', {
            email,
            code: values.verificationCode,
            password: values.password,
          });

          if (confirmResult.success) {
            await authorizeAndRedirect(values.password);
            return;
          }

          if (confirmResult.code === USER_ALREADY_CONFIRMED_CODE) {
            setOpen(true);
            return;
          }

          if (INVALID_INVITE_CODE_CODES.has(confirmResult.code)) {
            setError(true);
            return;
          }

          // Resent-invite emails use a temporary password instead of a confirmation code.
          await completeInviteWithTemporaryPassword(values.verificationCode, values.password);
        } catch (e) {
          console.log('Caught error:', e);
          if (e.name === 'NotAuthorizedException') {
            setOpen(true);
            return;
          }

          setError(true);
          errorAlert(e?.message);
        } finally {
          setLoading(false);
        }
      } else {
        if (!(email && values.verificationCode)) {
          setLoading(false);
          return;
        }

        try {
          await AmplifyAuth.forgotPasswordSubmit(email, values.verificationCode, values.password);
          history.push(ROUTE_PASSWORD_SUCCESS.path);
        } catch (e) {
          setError(e?.message);
          errorAlert(e?.message);
          catchYupErrors(e, setErrors, (err) => setStatus({ message: err.message }));
        } finally {
          setLoading(false);
        }
      }
    },
    [application, authorizeAndRedirect, completeInviteWithTemporaryPassword, email, errorAlert, history, isInviteFlow]
  );

  return (
    <AuthLayout>
      <SetPasswordForm onSubmit={handleSubmit} error={error} loading={loading} isInviteFlow={isInviteFlow} />
      <ModalDialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        disableContentSpacing
        title={`${trans("The 'Create Your Password' link has already been used.")}`}
      >
        <SpacingContainer px={6} py={4}>
          <Grid container spacing={2} ml={8} justifyContent="flex-start">
            <Grid item>
              <Typography variant="body1" weight={400}>
                It seems that the one-time link has already been used. Below are possible solutions.{' '}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="h5" weight={600} fontFamily="lexend">
                Solutions:
              </Typography>
              <ol>
                <li>
                  <Typography variant="body1" weight={400}>
                    First, try{' '}
                    <span
                      style={{ color: '#2445A7', cursor: 'pointer' }}
                      onClick={() => (window.location.href = '/sign-in')}
                    >
                      logging in
                    </span>{' '}
                    using the password you’ve already created for your USFCR account.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" weight={400}>
                    If you haven’t set a password, you can start over by clicking the
                    <span
                      style={{ color: '#2445A7', cursor: 'pointer' }}
                      onClick={() => (window.location.href = '/sign-in?forgotPassword=true')}
                    >
                      “Forgot your password?“
                    </span>{' '}
                    link on the Login page.
                  </Typography>
                </li>
              </ol>
            </Grid>
          </Grid>
        </SpacingContainer>
      </ModalDialog>
    </AuthLayout>
  );
};

export default SetPassword;
